import { useState, useEffect, useContext } from 'react';
import { useAuth } from '../contexts/AuthContext';
import AccountContext from '../contexts/AccountContext';
import { fetchDirectly } from '../common/apiUtils';

/**
 * Custom hook to fetch detailed sandbox information including environment warning messages
 * @returns {Object} { sandboxDetails, isLoading, error }
 */
export const useSandboxDetails = () => {
  const { getAccessTokenSilently } = useAuth();
  const { currentStateData } = useContext(AccountContext);
  const [sandboxDetails, setSandboxDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchSandboxDetails = async () => {
      // Only fetch if we have the necessary data
      if (!currentStateData?.org?.orgId || 
          !currentStateData?.product?.productId || 
          !currentStateData?.sandbox?.sandboxId) {
        setSandboxDetails(null);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const endpoint = `v1/org/${currentStateData.org.orgId}/product/${currentStateData.product.productId}/sandbox/${currentStateData.sandbox.sandboxId}`;
        const response = await fetchDirectly({
          token: await getAccessTokenSilently(),
          fullUrl: endpoint,
          method: "GET",
        });
        
        setSandboxDetails(response);
      } catch (err) {
        console.error('Error fetching sandbox details:', err);
        setError(err);
        setSandboxDetails(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSandboxDetails();
  }, [
    currentStateData?.org?.orgId,
    currentStateData?.product?.productId,
    currentStateData?.sandbox?.sandboxId,
    getAccessTokenSilently
  ]);

  return { sandboxDetails, isLoading, error };
};

export default useSandboxDetails;
